const DB_NAME = 'menuraq-offline-queue';
const STORE_NAME = 'actions';
const DB_VERSION = 1;

export type ActionScope = 'dashboard' | 'pos' | 'menu' | 'admin' | 'auth';

export type QueuedAction = {
  id: string;
  scope: ActionScope;
  type: string;
  payload: Record<string, unknown>;
  metadata: Record<string, unknown>;
  createdAt: number;
  attempts: number;
  maxAttempts: number;
  lastError?: string;
};

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('scope', 'scope', { unique: false });
        store.createIndex('createdAt', 'createdAt', { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T> | T,
): Promise<T> {
  const db = await openDb();
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, mode);
      const store = tx.objectStore(STORE_NAME);
      const result = fn(store);
      if (result instanceof IDBRequest) {
        result.onsuccess = () => resolve(result.result);
        result.onerror = () => reject(result.error);
      } else {
        resolve(result);
      }
      tx.oncomplete = () => {
        if (!(result instanceof IDBRequest)) resolve(result);
      };
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

export async function enqueueAction(
  record: Omit<QueuedAction, 'attempts' | 'lastError'>,
): Promise<string> {
  const id = record.id || crypto.randomUUID();
  await withStore('readwrite', (store) => {
    store.put({ ...record, id, attempts: 0, lastError: undefined });
  });
  return id;
}

export async function listActions(scope?: ActionScope): Promise<QueuedAction[]> {
  return withStore('readonly', (store) => {
    if (scope) {
      const index = store.index('scope');
      return index.getAll(scope);
    }
    return store.getAll();
  });
}

export async function listPendingActions(scope?: ActionScope): Promise<QueuedAction[]> {
  const all = await listActions(scope);
  return all
    .filter((a) => a.attempts < a.maxAttempts)
    .sort((a, b) => a.createdAt - b.createdAt);
}

export async function removeAction(id: string): Promise<void> {
  await withStore('readwrite', (store) => {
    store.delete(id);
  });
}

export async function markActionFailed(id: string, error: string): Promise<void> {
  await withStore('readwrite', (store) => {
    const request = store.get(id);
    request.onsuccess = () => {
      const action = request.result as QueuedAction | undefined;
      if (action) {
        store.put({ ...action, attempts: action.attempts + 1, lastError: error });
      }
    };
  });
}

export async function clearScope(scope: ActionScope): Promise<void> {
  const actions = await listActions(scope);
  await Promise.all(actions.map((a) => removeAction(a.id)));
}

export async function countPendingActions(): Promise<number> {
  const all = await listActions();
  return all.filter((a) => a.attempts < a.maxAttempts).length;
}

export async function countScopePending(scope: ActionScope): Promise<number> {
  const all = await listActions(scope);
  return all.filter((a) => a.attempts < a.maxAttempts).length;
}
